import { Box, Link as ChakraLink, HStack, VStack } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'
import type { NavItem } from './navConfig'

type SectionTabsProps = { items: NavItem[]; currentPath: string }

function isMatch(to: string, currentPath: string) {
  return currentPath === to || currentPath.startsWith(`${to}/`)
}

// Every navigable target in the tab bar, including the children hidden inside
// a grouped dropdown, so the active tab can be resolved by longest prefix.
function collectTargets(items: NavItem[]): string[] {
  const targets: string[] = []
  for (const item of items) {
    targets.push(item.to)
    for (const child of item.children ?? []) {
      targets.push(child.to)
    }
  }
  return targets
}

// When more than one target matches the current path as a prefix (e.g.
// '/factory' and '/factory/flow' both match '/factory/flow'), only the most
// specific (longest) match is treated as active.
function findActiveTo(items: NavItem[], currentPath: string) {
  let active: string | undefined
  let bestLen = -1
  for (const to of collectTargets(items)) {
    if (isMatch(to, currentPath) && to.length > bestLen) {
      active = to
      bestLen = to.length
    }
  }
  return active
}

const tabBaseProps = {
  px: 5,
  py: 2,
  borderRadius: 'md',
  fontSize: 'sm',
  letterSpacing: '0.02em',
  transition: 'color 0.18s ease, background-color 0.18s ease, transform 0.18s ease',
} as const

// Inactive tabs lift slightly and gain a subtle background on hover; the
// active tab stays put. Shared by the plain tabs and the grouped menu trigger.
const inactiveHover = {
  bg: 'bg.subtle',
  color: 'content.primary',
  textDecoration: 'none',
  transform: 'translateY(-1px)',
} as const

const activeHover = { textDecoration: 'none' } as const

function TabLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <ChakraLink
      asChild
      aria-current={active ? 'page' : undefined}
      {...tabBaseProps}
      fontWeight={active ? 'semibold' : 'medium'}
      bg={active ? 'content.primary' : 'transparent'}
      color={active ? 'bg' : 'content.secondary'}
      _hover={active ? activeHover : inactiveHover}
    >
      <Link to={item.to as string}>{item.label}</Link>
    </ChakraLink>
  )
}

// Second-level tab (under an expanded group). Subtler than the primary pill:
// a light bordered chip so the two tab levels read as a hierarchy.
function SubTabLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <ChakraLink
      asChild
      aria-current={active ? 'page' : undefined}
      px={4}
      py={1.5}
      borderRadius="md"
      fontSize="sm"
      letterSpacing="0.02em"
      borderWidth="1px"
      transition="color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease"
      fontWeight={active ? 'semibold' : 'medium'}
      bg={active ? 'bg.subtle' : 'transparent'}
      color={active ? 'content.primary' : 'content.secondary'}
      borderColor={active ? 'border' : 'transparent'}
      _hover={{ bg: 'bg.subtle', color: 'content.primary', textDecoration: 'none' }}
    >
      <Link to={item.to as string}>{item.label}</Link>
    </ChakraLink>
  )
}

// A tab that groups several pages behind one dropdown menu. The label is still
// a real link (to the group's landing page); the menu opens on hover/focus and
// mirrors PrimaryNav's keyboard-accessible flyout (relatedTarget-guarded blur,
// Escape to close, aria-haspopup/expanded).
function TabMenu({
  item,
  active,
  activeTo,
}: {
  item: NavItem
  active: boolean
  activeTo: string | undefined
}) {
  const [open, setOpen] = useState(false)
  const children = item.children ?? []
  return (
    <Box
      position="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setOpen(false)
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setOpen(false)
        }
      }}
    >
      <ChakraLink
        asChild
        aria-current={active ? 'page' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        {...tabBaseProps}
        display="inline-flex"
        alignItems="center"
        gap={1}
        fontWeight={active ? 'semibold' : 'medium'}
        bg={active ? 'content.primary' : 'transparent'}
        color={active ? 'bg' : 'content.secondary'}
        _hover={active ? activeHover : inactiveHover}
        onFocus={() => setOpen(true)}
      >
        <Link to={item.to as string}>
          {item.label}
          <ChevronDownIcon size={14} />
        </Link>
      </ChakraLink>
      {open && (
        <Box
          position="absolute"
          top="100%"
          left="50%"
          transform="translateX(-50%)"
          zIndex={10}
          minW="200px"
          py={2}
          mt={1}
          bg="bg.panel"
          borderWidth="1px"
          borderColor="border"
          borderRadius="md"
          boxShadow="md"
        >
          {children.map((child) => {
            const childActive = child.to === activeTo
            return (
              <ChakraLink
                key={child.to}
                asChild
                aria-current={childActive ? 'page' : undefined}
                display="block"
                px={4}
                py={2}
                fontSize="sm"
                fontWeight={childActive ? 'semibold' : 'medium'}
                color={childActive ? 'content.primary' : 'content.secondary'}
                bg={childActive ? 'bg.subtle' : 'transparent'}
                _hover={{ bg: 'bg.subtle', color: 'content.primary', textDecoration: 'none' }}
              >
                <Link to={child.to as string}>{child.label}</Link>
              </ChakraLink>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export function SectionTabs({ items, currentPath }: SectionTabsProps) {
  const activeTo = findActiveTo(items, currentPath)
  // A grouped item is active when the active target is its own landing page or
  // any of its children.
  const isActive = (item: NavItem) =>
    item.to === activeTo || (item.children ?? []).some((c) => c.to === activeTo)
  // An active `expandTabs` group surfaces its children as a second tab row.
  const expandedGroup = items.find((it) => it.expandTabs && it.children && isActive(it))

  return (
    <VStack gap={3} mb={6}>
      <HStack gap={3} justify="center">
        {items.map((item) => {
          const active = isActive(item)
          // Only non-expand groups render as a dropdown; expand groups (and
          // plain items) render as a normal tab.
          return item.children && !item.expandTabs ? (
            <TabMenu key={item.to} item={item} active={active} activeTo={activeTo} />
          ) : (
            <TabLink key={item.to} item={item} active={active} />
          )
        })}
      </HStack>
      {expandedGroup?.children ? (
        <HStack gap={2} justify="center">
          {expandedGroup.children.map((child) => (
            <SubTabLink key={child.to} item={child} active={child.to === activeTo} />
          ))}
        </HStack>
      ) : null}
    </VStack>
  )
}
